import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Logo from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';

const { FiMail, FiLock, FiAlertTriangle, FiArrowLeft, FiShield, FiKey } = FiIcons;

const LoginPage = () => {
  const { t } = useLanguage();
  const [step, setStep] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authMeta, setAuthMeta] = useState(null);
  
  const { initiateLogin, verifyOTP } = useAuth();
  const navigate = useNavigate();

  const handleCredentialSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await initiateLogin(email, password);
      setAuthMeta(response);
      setStep('otp');
    } catch (err) {
      setError(t.login.errors.invalid);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const fullCode = otp.join('');
    if (fullCode.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      await verifyOTP(email, fullCode);
      navigate('/dashboard');
    } catch (err) {
      setError(t.login.errors.otp);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="fixed top-8 right-8">
        <LanguageSwitcher />
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
        
        {step === 'otp' && authMeta && !authMeta.sentRealEmail && (
          <div className="mb-4 p-4 rounded-xl border bg-blue-50 border-blue-200 text-blue-800">
            <div className="flex gap-3 items-center">
              <SafeIcon icon={FiShield} className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <p className="text-sm font-medium">Dev Mode Code: <span className="font-mono font-bold tracking-widest">{authMeta.code}</span></p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="text-center mb-8">
            <Logo className="justify-center mb-6" />
            <h2 className="text-2xl font-bold text-gray-900">
              {step === 'credentials' ? t.login.title : t.login.verifyTitle}
            </h2>
            <p className="text-gray-500 mt-2 text-sm">
              {step === 'credentials' ? t.login.sub : `${t.login.verifySub} ${email}`}
            </p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg text-sm flex items-start">
              <SafeIcon icon={FiAlertTriangle} className="mr-2 h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 'credentials' ? (
            <form onSubmit={handleCredentialSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">{t.login.email}</label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">{t.login.password}</label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3.5 rounded-xl font-bold transition-all shadow-md">
                {loading ? '...' : t.login.continue}
              </button>
              <p className="text-center text-xs text-gray-500">
                {t.login.noAccount} <Link to="/signup" className="text-blue-600 font-bold">{t.login.createNow}</Link>
              </p>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <div className="flex justify-between gap-2">
                {otp.map((digit, index) => (
                  <input key={index} id={`otp-${index}`} type="text" maxLength="1" value={digit} onChange={(e) => handleOtpChange(index, e.target.value)} className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-200 rounded-xl focus:border-blue-600 bg-gray-50 outline-none" />
                ))}
              </div>
              <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3.5 rounded-xl font-bold transition-all shadow-md">
                <SafeIcon icon={FiKey} className="mr-2 inline" />
                {t.login.verifyBtn}
              </button>
              <button type="button" onClick={() => setStep('credentials')} className="w-full text-sm text-gray-500 font-medium flex items-center justify-center">
                <SafeIcon icon={FiArrowLeft} className="mr-2" /> {t.login.back}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;